import { DateTime, Info } from "luxon"
import { lastFullMoonBefore, moonPhaseFromAnchor } from "./lunar"

// ISO weekday (1 = Monday … 7 = Sunday) the week starts on for a locale.
// Not just Monday/Sunday: e.g. ar-SA weeks start on Saturday (6).
export function getWeekStart(locale) {
  try {
    const ws = Info.getStartOfWeek({ locale })
    if (ws >= 1 && ws <= 7) return ws
  } catch (_) {
    // fall through
  }
  try {
    const l = new Intl.Locale(locale)
    const info = typeof l.getWeekInfo === "function" ? l.getWeekInfo() : l.weekInfo
    if (info && info.firstDay >= 1 && info.firstDay <= 7) return info.firstDay
  } catch (_) {
    // fall through
  }
  return 1
}

// Builds the last lunar cycle (~30 days ending today) as full weeks.
// Pure of React; DateTime/locale/weekStart are injectable for tests.
export default function buildMoonCalendar({ today = DateTime.local(), locale = "en", weekStart = getWeekStart(locale) } = {}) {
  const zone = today.zone
  today = today.startOf("day")

  const rowStart = d => d.minus({ days: (d.weekday - weekStart + 7) % 7 })
  const gridStart = rowStart(today.minus({ days: 29 }))
  const gridEnd = rowStart(today).plus({ days: 6 })

  const fullMoonInstant = lastFullMoonBefore(today.endOf("day").toJSDate())
  const fullMoonDate = DateTime.fromJSDate(fullMoonInstant, { zone }).startOf("day")

  // The grid spans up to ~36 days and can contain a second full moon.
  const fullMoonDays = new Set()
  for (let cursor = gridEnd.endOf("day").toJSDate(); ;) {
    const instant = lastFullMoonBefore(cursor)
    const day = DateTime.fromJSDate(instant, { zone }).startOf("day")
    if (day < gridStart) break
    fullMoonDays.add(day.toISODate())
    cursor = new Date(instant.getTime() - 1)
  }

  // Formatters pinned to UTC and fed UTC-constructed dates so the label never
  // shifts across the system timezone.
  const weekdayFormat = new Intl.DateTimeFormat(locale, { weekday: "short", timeZone: "UTC" })
  const monthFormat = new Intl.DateTimeFormat(locale, { month: "short", timeZone: "UTC" })

  // 2024-01-01 was a Monday, so day-of-month n has ISO weekday n.
  const weekdays = Array.from({ length: 7 }, (_, i) => {
    const isoWeekday = ((weekStart - 1 + i) % 7) + 1
    return weekdayFormat.format(new Date(Date.UTC(2024, 0, isoWeekday)))
  })

  const weeks = []
  for (let start = gridStart; start <= gridEnd; start = start.plus({ days: 7 })) {
    const row = []
    for (let i = 0; i < 7; i++) {
      const day = start.plus({ days: i })
      const showMonth = day.day === 1 || (weeks.length === 0 && i === 0)
      row.push({
        iso: day.toISODate(),
        dayNumber: day.day,
        showMonth,
        monthLabel: showMonth ? monthFormat.format(new Date(Date.UTC(day.year, day.month - 1, day.day))) : undefined,
        // phase of that night, evaluated at 21:00 local
        phase: moonPhaseFromAnchor(day.set({ hour: 21 }).toJSDate(), fullMoonInstant),
        isFullMoon: fullMoonDays.has(day.toISODate()),
        isToday: day.hasSame(today, "day"),
        isFuture: day > today,
      })
    }
    weeks.push(row)
  }

  return {
    weekdays,
    weeks,
    daysSinceFullMoon: Math.round(today.diff(fullMoonDate, "days").days),
    fullMoonDate,
  }
}
